import * as monthUtils from '@actual-app/core/shared/months';
import type { IntegerAmount } from '@actual-app/core/shared/util';

import {
  caracterizarSerie,
  mesDeAccion,
  redondearA5,
} from '#components/mobile/analisis/motor';
import {
  cuotaMensual,
  proximoVencimiento,
  sumarMeses,
} from '#components/mobile/budget/objetivos';
import type { Objetivo } from '#components/mobile/budget/objetivos';
import {
  esPuntualEnSerie,
  mad,
  mediana,
  MINIMO_MESES_DISPERSION,
  recortarInicioSinDatos,
  referenciaHabitual,
  umbralAtipico,
} from '#components/mobile/informes/estadisticaRobusta';
import { color } from '#components/mobile/ui/tokens';

/**
 * Cálculos puros de la ficha de categoría («Diseño 1 · Ficha única»): estado
 * del objetivo, banda de lo habitual, series de las gráficas y las reglas
 * sencillas del Asesor propias de la ficha. Sin React ni i18n.
 */

// ---------------------------------------------------------------------------
// Objetivo
// ---------------------------------------------------------------------------

export type EstadoObjetivo = 'rumbo' | 'atrasado' | 'cumplido';

export type ResumenObjetivoFicha = {
  /** Avance 0..1 (anillo y barra). */
  progreso: number;
  /** Lo que falta (sobre el saldo en metas con fecha; sobre el mes si no). */
  falta: IntegerAmount;
  /** Cuota aproximada al mes (la de `runBy` de Actual en metas con fecha). */
  cuota: IntegerAmount;
  /** Meses que quedan hasta la fecha (incluido el actual), si tiene fecha. */
  meses: number | null;
  estado: EstadoObjetivo;
  /** El objetivo se mide sobre el saldo (meta con fecha o `long-goal`). */
  deSaldo: boolean;
  /** Próxima fecha (las que se repiten saltan a la siguiente vuelta). */
  fecha: string | null;
  /** (a) Sobrante sobre la meta: saldo − meta. Se puede mover sin riesgo. */
  sobrante: IntegerAmount;
  /**
   * Lo que deberías llevar a estas alturas del ciclo (meta × meses
   * transcurridos / meses del ciclo). Solo en objetivos que se repiten.
   */
  ritmo: IntegerAmount | null;
  /** (b) Adelanto sobre el ritmo: saldo − ritmo. Habría que reponerlo. */
  adelanto: IntegerAmount;
  /** Cuota normal de un ciclo entero (meta / meses del ciclo). */
  cuotaNormal: IntegerAmount | null;
  /**
   * Lo que mide el anillo («X % de meta»): la meta entera en metas con fecha;
   * en las de cada mes o semana, lo pedido este mes (o lo normal, si este mes
   * no pide nada).
   */
  meta: IntegerAmount;
};

/** Semanas medias por mes (52/12). */
const SEMANAS_MES = 52 / 12;

function fraccion(parte: number, total: number): number {
  if (total <= 0) {
    return 0;
  }
  return Math.max(0, Math.min(1, parte / total));
}

export function esObjetivoDeSaldo(
  objetivo: Objetivo | null,
  longGoal = false,
): boolean {
  return longGoal || objetivo?.tipo === 'una-vez' || objetivo?.tipo === 'anual';
}

/**
 * Estado del objetivo en el mes visto:
 * - Metas con fecha (una vez / cada año): la fecha de las que se repiten
 *   salta a la próxima vuelta en cuanto pasa su mes (como `runBy` de
 *   Actual). Cuota = (meta − saldo con el que se empezó el mes) / meses que
 *   quedan, la misma fórmula de `runBy`. Cumplido si el saldo llega a la
 *   meta; adelantado si supera el ritmo del ciclo; en rumbo si lo asignado
 *   cubre la cuota; si no, atrasado.
 * - Cada mes / semana: avance = asignado / lo pedido este mes, la misma
 *   vara que la fila del Plan (`estadoFila`). En «Rellenar hasta X» lo
 *   pedido es `goal-<id>` de Actual (X − lo traído), que es 0 o negativo si
 *   ya venía lleno: entonces este mes no pide nada (nunca una cuota
 *   negativa). En rumbo si no falta nada este mes.
 */
export function resumenObjetivo({
  objetivo,
  saldo,
  asignado,
  actividad = 0,
  goal,
  month,
}: {
  objetivo: Objetivo;
  saldo: IntegerAmount;
  asignado: IntegerAmount;
  /** Actividad del mes (negativa si es gasto). */
  actividad?: IntegerAmount;
  /** `goal-<id>` de la hoja (lo que piden las plantillas este mes). */
  goal: IntegerAmount | null;
  month: string;
}): ResumenObjetivoFicha {
  if (objetivo.tipo === 'una-vez' || objetivo.tipo === 'anual') {
    const meta = objetivo.importe;
    const fecha = proximoVencimiento(objetivo, `${month}-01`);
    // `fromLastMonth` de Actual: lo que traía la categoría del mes pasado.
    const delMesPasado = Math.max(0, saldo - asignado - actividad);
    // Un solo modelo (el de YNAB / `runBy`): la cuota del mes es la que
    // calcula Actual (`goal-<id>`, recién refrescada con `refresh-goals`);
    // si aún no está, la misma fórmula: (meta − lo traído) / meses.
    const cuota = goal ?? cuotaMensual(meta, delMesPasado, fecha, month);
    const meses = Math.max(
      0,
      monthUtils.differenceInCalendarMonths(fecha.slice(0, 7), month) + 1,
    );
    const falta = Math.max(0, meta - saldo);
    const sobrante = Math.max(0, saldo - meta);
    let ritmo: IntegerAmount | null = null;
    let cuotaNormal: IntegerAmount | null = null;
    if (objetivo.tipo === 'anual') {
      const ciclo = objetivo.cadaMeses;
      const inicio = sumarMeses(fecha, -ciclo).slice(0, 7);
      const transcurridos = Math.min(
        ciclo,
        Math.max(0, monthUtils.differenceInCalendarMonths(month, inicio)),
      );
      ritmo = Math.round((meta * transcurridos) / ciclo);
      cuotaNormal = Math.round(meta / ciclo);
    }
    const adelanto = ritmo != null ? Math.max(0, saldo - ritmo) : 0;
    // Ir por delante del calendario ya baja la cuota: no es otro estado.
    const estado: EstadoObjetivo =
      falta === 0 ? 'cumplido' : asignado >= cuota ? 'rumbo' : 'atrasado';
    return {
      progreso: fraccion(saldo, meta),
      falta,
      cuota,
      meses,
      estado,
      deSaldo: true,
      fecha,
      sobrante,
      ritmo,
      adelanto,
      cuotaNormal,
      meta,
    };
  }
  const normal =
    objetivo.tipo === 'semanal'
      ? Math.round(objetivo.importe * SEMANAS_MES)
      : objetivo.importe;
  // Sin `goal` en la hoja aún, la misma cuenta que la plantilla de Actual.
  const traido = Math.max(0, saldo - asignado - actividad);
  const pedido =
    goal ??
    (objetivo.modo === 'rellenar' ? Math.max(0, normal - traido) : normal);
  const cuota = Math.max(0, pedido);
  const falta = Math.max(0, cuota - asignado);
  return {
    progreso: cuota > 0 ? fraccion(asignado, cuota) : 1,
    falta,
    cuota,
    meses: null,
    estado: falta === 0 ? 'rumbo' : 'atrasado',
    deSaldo: false,
    fecha: null,
    sobrante: 0,
    ritmo: null,
    adelanto: 0,
    cuotaNormal: normal !== cuota ? normal : null,
    meta: cuota > 0 ? cuota : normal,
  };
}

// ---------------------------------------------------------------------------
// Evolución
// ---------------------------------------------------------------------------

/** Meses completos anteriores a `month` (los `n` últimos, en orden). */
export function mesesAnteriores(month: string, n: number): string[] {
  return monthUtils.rangeInclusive(
    monthUtils.subMonths(month, n),
    monthUtils.subMonths(month, 1),
  );
}

/**
 * Banda de lo habitual: mediana ± MAD. En una categoría irregular (pocos
 * meses con gasto) se calcula solo con los meses con gasto; si no, con todos
 * (con los 12 meses, la mediana de una irregular sería 0).
 */
export function bandaHabitual(
  gasto: readonly number[],
  irregular: boolean,
): { mediana: number; min: number; max: number } {
  const valores = irregular ? gasto.filter(v => v > 0) : [...gasto];
  const centro = mediana(valores);
  const d = mad(valores, centro);
  return { mediana: centro, min: Math.max(0, centro - d), max: centro + d };
}

/**
 * Banda de la gráfica con la MISMA referencia que usa el motor del Asesor
 * (`caracterizarSerie`): con tendencia, la referencia frente a la que se
 * mide el cambio; si no, lo habitual de 12 meses (sin atípicos). En una
 * irregular, mediana ± MAD de los meses con gasto (últimos 12).
 */
export function bandaFicha(gasto: readonly number[]): {
  mediana: number;
  min: number;
  max: number;
} {
  const c = caracterizarSerie(gasto);
  if (c.irregular) {
    return bandaHabitual(gasto.slice(-12), true);
  }
  const ref = c.tendencia ? c.tendencia.referencia : c.habitual;
  return {
    mediana: ref.referencia,
    min: Math.max(0, ref.referencia - ref.mad),
    max: ref.referencia + ref.mad,
  };
}

/** Meses con datos por debajo de los cuales no se habla de «lo habitual». */
export const MINIMO_MESES_HABITUAL = 3;

export type HistoriaGasto = 'vacia' | 'pocos-datos' | 'suficiente';

/**
 * Cuánta historia de gasto hay (desde el primer mes con gasto) para la
 * gráfica de gasto: sin ninguno, estado vacío; con menos de
 * `MINIMO_MESES_HABITUAL`, «pocos datos todavía» en lugar de la banda.
 */
export function historiaGasto(gasto: readonly number[]): HistoriaGasto {
  const meses = recortarInicioSinDatos(gasto).length;
  if (meses === 0 || !gasto.some(v => v > 0)) {
    return 'vacia';
  }
  return meses < MINIMO_MESES_HABITUAL ? 'pocos-datos' : 'suficiente';
}

/** Gasto irregular: ≤ la mitad de los meses con gasto (como el motor). */
export function esIrregular(gasto: readonly number[]): boolean {
  const conGasto = gasto.filter(v => v > 0).length;
  return gasto.length >= 6 && conGasto > 0 && conGasto <= gasto.length / 2;
}

/** Paso «bonito» del eje (1, 2, 5 × 10ⁿ) para unas `marcas` divisiones. */
export function pasoEje(maximo: number, marcas = 3): number {
  if (maximo <= 0) {
    return 1;
  }
  const bruto = maximo / marcas;
  const potencia = 10 ** Math.floor(Math.log10(bruto));
  const n = bruto / potencia;
  const factor = n <= 1 ? 1 : n <= 2 ? 2 : n <= 5 ? 5 : 10;
  return factor * potencia;
}

/**
 * Mapa de estacionalidad: filas de 12 meses que acaban en el mes anterior a
 * `month` (la última fila) hacia atrás.
 */
export function filasEstacionalidad(month: string, anos: number): string[][] {
  const filas: string[][] = [];
  for (let a = anos - 1; a >= 0; a--) {
    const fin = monthUtils.subMonths(month, 1 + a * 12);
    filas.push(monthUtils.rangeInclusive(monthUtils.subMonths(fin, 11), fin));
  }
  return filas;
}

/**
 * Los dos meses del año (por posición en la fila) que más pesan en el gasto
 * de todas las filas, y su parte del total. `null` si no hay gasto.
 */
export function mesesFuertes(
  valores: readonly (readonly number[])[],
): { columnas: [number, number]; parte: number } | null {
  const porColumna = Array.from({ length: 12 }, (_, i) =>
    valores.reduce((s, fila) => s + Math.max(0, fila[i] ?? 0), 0),
  );
  const total = porColumna.reduce((a, b) => a + b, 0);
  if (total <= 0) {
    return null;
  }
  const orden = porColumna
    .map((v, i) => [v, i] as const)
    .sort((a, b) => b[0] - a[0]);
  const columnas: [number, number] = [orden[0][1], orden[1][1]];
  return { columnas, parte: (orden[0][0] + orden[1][0]) / total };
}

// ---------------------------------------------------------------------------
// Asesor de la ficha
// ---------------------------------------------------------------------------

/** Meses con datos mínimos para la regla de «no seguidos». */
export const MINIMO_HISTORIA_SEGUIDOS = 6;

export type ConsejoAsigna = {
  tipo: 'asigna';
  /** Lo que conviene asignar al mes. */
  importe: IntegerAmount;
  /** De dónde sale: el objetivo o el gasto habitual. */
  origen: 'objetivo' | 'habitual';
  /**
   * Mínimo y máximo de los meses con gasto, sin los gastos puntuales (null
   * si no hubo gasto).
   */
  minimo: IntegerAmount | null;
  maximo: IntegerAmount | null;
  /**
   * Media de los meses con gasto: la MISMA base que el mínimo y el máximo
   * (con todos los meses, la media podía quedar por debajo del mínimo).
   */
  media: IntegerAmount;
  /** Meses (índices de `gasto`) que no cuentan por ser un gasto puntual. */
  puntuales: number[];
};

export type ConsejoNoSeguidos = {
  tipo: 'no-seguidos';
  conGasto: number;
  meses: number;
  /** Única racha de dos o más meses seguidos con gasto (índices). */
  excepcion: { desde: number; hasta: number } | null;
};

/**
 * Historial de gasto mensual más largo (hasta 36 meses) que acaba en los
 * mismos meses que la ventana `gasto`: sirve para ver si un mes alto se
 * repite (otra temporada, otro año).
 */
export type GastoLargo = {
  meses: readonly string[];
  gasto: readonly number[];
};

function mesesFicticios(n: number): string[] {
  return Array.from(
    { length: n },
    (_, k) =>
      `${2000 + Math.floor(k / 12)}-${String((k % 12) + 1).padStart(2, '0')}`,
  );
}

/**
 * Meses de gasto puntual (índices de `gasto`) con la estadística robusta
 * de Informes, en versión conservadora (`esPuntualEnSerie`): por ENCIMA de
 * lo habitual más allá del umbral (max(3·MAD, 25 %, 10 €)), más de 3 veces
 * lo habitual (5 si el historial no llega al mismo mes del año anterior) y
 * sin repetirse: ni el mes anterior ni el siguiente altos (julio y agosto
 * en vacaciones), ni la misma temporada de otro año alta (gas en invierno),
 * ni otro mes de al menos 2 veces lo habitual en `historia`. Lo habitual
 * sale de los meses con gasto de `gasto`; con menos de 3, de `referencia`
 * (la cuota del objetivo) si la hay. Sin `historia`, solo se mira `gasto`
 * (meses seguidos). Nunca deja sin ningún mes.
 */
export function mesesPuntuales(
  gasto: readonly number[],
  referencia: IntegerAmount | null = null,
  historia?: GastoLargo,
): number[] {
  const conGasto = gasto.filter(v => v > 0);
  let centro: number;
  let umbral: number;
  if (conGasto.length >= MINIMO_MESES_DISPERSION) {
    const ref = referenciaHabitual(conGasto);
    centro = ref.mediana;
    umbral = ref.umbral;
  } else if (referencia != null && referencia > 0) {
    centro = referencia;
    umbral = umbralAtipico(referencia, 0);
  } else {
    return [];
  }
  const h =
    historia && historia.gasto.length >= gasto.length
      ? historia
      : { meses: mesesFicticios(gasto.length), gasto };
  const serie = h.gasto.map((importe, k) => ({ mes: h.meses[k], importe }));
  const desfase = h.gasto.length - gasto.length;
  const puntuales = gasto
    .map((_, i) => i)
    .filter(i => esPuntualEnSerie(i + desfase, serie, centro, umbral));
  return puntuales.length < conGasto.length ? puntuales : [];
}

/**
 * (a) «Asigna X; gastas mínimo Y y máximo Z (media W)». `importe` es la
 * cuota del objetivo o, sin objetivo, lo habitual. Mínimo, máximo y media
 * sin los gastos puntuales (`mesesPuntuales`, que mira `historia` para ver
 * si un mes alto se repite). `null` si no hay nada que
 * proponer (sin objetivo ni gasto).
 */
export function consejoAsigna(
  gasto: readonly number[],
  importe: IntegerAmount | null,
  origen: ConsejoAsigna['origen'],
  historia?: GastoLargo,
): ConsejoAsigna | null {
  if (importe == null || importe <= 0) {
    return null;
  }
  const puntuales = mesesPuntuales(
    gasto,
    origen === 'objetivo' ? importe : null,
    historia,
  );
  const fuera = new Set(puntuales);
  const conGasto = gasto.filter((v, i) => v > 0 && !fuera.has(i));
  const media =
    conGasto.length > 0
      ? Math.round(conGasto.reduce((a, b) => a + b, 0) / conGasto.length)
      : 0;
  return {
    tipo: 'asigna',
    importe,
    origen,
    minimo: conGasto.length > 0 ? Math.min(...conGasto) : null,
    maximo: conGasto.length > 0 ? Math.max(...conGasto) : null,
    media,
    puntuales,
  };
}

/** Rachas de meses seguidos con gasto: `[inicio, fin]` (índices). */
export function rachasConGasto(gasto: readonly number[]): [number, number][] {
  const rachas: [number, number][] = [];
  let inicio = -1;
  gasto.forEach((v, i) => {
    if (v > 0) {
      if (inicio < 0) {
        inicio = i;
      }
    } else if (inicio >= 0) {
      rachas.push([inicio, i - 1]);
      inicio = -1;
    }
  });
  if (inicio >= 0) {
    rachas.push([inicio, gasto.length - 1]);
  }
  return rachas;
}

/**
 * (b) «No la gastas dos meses seguidos: quizá no haga falta financiarla cada
 * mes». Con ≥ 6 meses de historia (desde el primer mes con gasto), algún mes
 * sin gasto, al menos dos con gasto y como mucho una racha de dos o más
 * meses seguidos con gasto (la excepción, p. ej. julio-agosto).
 */
export function consejoNoSeguidos(
  gasto: readonly number[],
): ConsejoNoSeguidos | null {
  const historia = recortarInicioSinDatos(gasto);
  if (historia.length < MINIMO_HISTORIA_SEGUIDOS) {
    return null;
  }
  const desplazamiento = gasto.length - historia.length;
  const conGasto = historia.filter(v => v > 0).length;
  if (conGasto < 2 || conGasto === historia.length) {
    return null;
  }
  const largas = rachasConGasto(historia).filter(([a, b]) => b > a);
  if (largas.length > 1) {
    return null;
  }
  return {
    tipo: 'no-seguidos',
    conGasto,
    meses: historia.length,
    excepcion: largas[0]
      ? {
          desde: largas[0][0] + desplazamiento,
          hasta: largas[0][1] + desplazamiento,
        }
      : null,
  };
}

/** Meses con datos (desde el primero con gasto) sobre los que se opina. */
export function mesesConHistoria(gasto: readonly number[]): number {
  return recortarInicioSinDatos(gasto).length;
}

export { mesDeAccion };

export type NivelActual = {
  /** Lo que conviene asignar: el nivel actual redondeado a 5 € arriba. */
  importe: IntegerAmount;
  /** Nivel sin redondear. */
  nivel: IntegerAmount;
  /** Mínimo y máximo de los meses de ese nivel. */
  minimo: IntegerAmount;
  maximo: IntegerAmount;
  /** Índice (en `gasto`) del primer mes del nivel nuevo, si cambió. */
  desde: number | null;
  /** Solo los dos últimos meses en otro nivel: «ojo, dos meses por encima». */
  dosMeses: 'sube' | 'baja' | null;
  /** El último mes fue un gasto puntual: su importe (no cuenta). */
  puntual: IntegerAmount | null;
};

/**
 * Nivel ACTUAL de gasto de una categoría sin objetivo, robusto ante gastos
 * puntuales (el mismo criterio que el motor, `caracterizarSerie`):
 * - base: mediana robusta de los últimos 12 meses sin los atípicos;
 * - solo cambia con un cambio PERSISTENTE (≥ 3 meses seguidos coherentes,
 *   `cambioDeNivel`): entonces, la mediana de esos meses.
 * Un pico (un móvil de 400 €) nunca mueve la recomendación. Mínimo y máximo
 * solo de los meses de ese nivel.
 */
export function nivelActual(gasto: readonly number[]): NivelActual | null {
  const c = caracterizarSerie(gasto);
  if (c.primerMes < 0) {
    return null;
  }
  let meses: number[];
  let desde: number | null = null;
  if (c.tendencia) {
    desde = Math.max(c.primerMes, gasto.length - c.tendencia.meses);
    meses = gasto.slice(desde);
  } else {
    const ultimos = recortarInicioSinDatos(gasto).slice(-12);
    meses = ultimos.filter((_, i) => !c.habitual.atipicos[i]);
  }
  if (meses.length === 0 || c.vigente <= 0) {
    return null;
  }
  return {
    importe: redondearA5(c.vigente, 'ceil'),
    nivel: c.vigente,
    minimo: Math.min(...meses),
    maximo: Math.max(...meses),
    desde,
    dosMeses: c.dosMeses,
    puntual: c.ultimoAtipico ? (gasto[gasto.length - 1] ?? null) : null,
  };
}

/** «Sobran X sobre la meta: puedes devolverlos o usarlos». */
export function consejoSobrante(
  sobrante: IntegerAmount,
): { tipo: 'sobrante'; importe: IntegerAmount } | null {
  return sobrante > 0 ? { tipo: 'sobrante', importe: sobrante } : null;
}

/** Cuánto puede subir la cuota tras dormir (× la cuota normal). */
export const FACTOR_CUOTA_DORMIR = 1.5;

/**
 * «Puedes dormirla N meses: después la cuota subiría a unos X €/mes». Ir
 * por delante ya baja la cuota; dormir N meses (sin asignar) la sube a
 * X = (meta − saldo) / (meses que quedan tras este − N). Se propone el mayor
 * N con X ≤ 1,5 × la cuota normal; `null` si ni un mes cabe.
 */
export function sugerenciaDormir({
  falta,
  meses,
  cuotaNormal,
}: {
  /** Lo que falta hasta la meta (meta − saldo). */
  falta: IntegerAmount;
  /** Meses hasta la fecha, incluido el actual. */
  meses: number | null;
  cuotaNormal: IntegerAmount | null;
}): { meses: number; cuotaDespues: IntegerAmount } | null {
  if (!meses || !cuotaNormal || cuotaNormal <= 0) {
    return null;
  }
  const quedan = meses - 1;
  const tope = cuotaNormal * FACTOR_CUOTA_DORMIR;
  for (let n = quedan - 1; n >= 1; n--) {
    const cuotaDespues = Math.round(falta / (quedan - n));
    if (cuotaDespues <= tope) {
      return { meses: n, cuotaDespues };
    }
  }
  return null;
}

/**
 * Meses a los que se puede dormir una categoría (además de «este mes»): hasta
 * el cobro (objetivo con fecha posterior) y hasta <mes> cuando hay adelanto
 * (`sugerenciaDormir`). Lo usa la ficha («Suficiente hasta…»).
 */
export function opcionesSueno(
  resumen: ResumenObjetivoFicha | null,
  month: string,
): { mesCobro: string | null; mesAdelanto: string | null } {
  const mesCobro =
    resumen?.fecha && resumen.fecha.slice(0, 7) > month
      ? resumen.fecha.slice(0, 7)
      : null;
  const dormirN =
    resumen && resumen.falta > 0
      ? sugerenciaDormir({
          falta: resumen.falta,
          meses: resumen.meses,
          cuotaNormal: resumen.cuotaNormal,
        })
      : null;
  return {
    mesCobro,
    mesAdelanto: dormirN ? monthUtils.addMonths(month, dormirN.meses) : null,
  };
}

// ---------------------------------------------------------------------------
// B4 · H46 y H44
// ---------------------------------------------------------------------------

/**
 * Color del anillo y de la barra de progreso: el del estado, igual que su
 * píldora (atrasado = aviso, cumplido = acento, en rumbo = ok, pausada =
 * apagado). Nunca un anillo verde junto a «Atrasado».
 */
export function colorProgreso(
  estado: EstadoObjetivo | null,
  ignorada = false,
): string {
  if (ignorada) {
    return color.fg3;
  }
  switch (estado) {
    case 'atrasado':
      return color.warn;
    case 'cumplido':
      return color.accent;
    case 'rumbo':
      return color.ok;
    default:
      return color.accent;
  }
}

/**
 * Importes que el editor del objetivo ofrece con «Usar» (como el «Set target
 * to» de YNAB): la media mensual de gasto (últimos 12 meses con historia) y
 * lo gastado el mes pasado (el último de la serie). 0 si no hay dato.
 */
export function sugerenciasImporte(gasto: readonly number[]): {
  media: IntegerAmount;
  mesPasado: IntegerAmount;
} {
  const conHistoria = recortarInicioSinDatos(gasto).slice(-12);
  const media =
    conHistoria.length === 0
      ? 0
      : Math.round(conHistoria.reduce((a, b) => a + b, 0) / conHistoria.length);
  const ultimo = gasto.length > 0 ? gasto[gasto.length - 1] : 0;
  return { media: Math.max(0, media), mesPasado: Math.max(0, ultimo) };
}
